"use client";

import * as React from "react";
import { motion } from "motion/react";
import {
  Globe,
  Cpu,
  Trophy,
  Users,
  Crown,
  Award,
  Activity,
  MapPin,
  Zap,
  Target,
  Briefcase,
  Heart,
} from "lucide-react";
import { Section } from "@/components/layout/section";
import { SectionHeader } from "@/components/ui/section-header";
import { AnimatedStat } from "@/components/ui/animated-stat";
import {
  RadialBarChart,
  RadialBar,
  ResponsiveContainer,
  PolarAngleAxis,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Cell,
} from "recharts";

const METRICS = [
  { value: 15, suffix: "+", label: "Years", sub: "Continuous sovereign operations", icon: Activity, cls: "red" },
  { value: 18, suffix: "", label: "Countries", sub: "Active operations · 3 continents", icon: Globe, cls: "cyan" },
  { value: 30, suffix: "+", label: "Client Countries", sub: "Governments + leaders", icon: MapPin, cls: "denim" },
  { value: 60, suffix: "+", label: "Services", sub: "Across 5 strategic domains", icon: Cpu, cls: "purple" },
  { value: 5, suffix: "", label: "Domains", sub: "Penta P's framework", icon: Target, cls: "amber-gold" },
  { value: 83.3, suffix: "%", label: "Success", sub: "15 wins of 18 engagements", icon: Trophy, cls: "emerald" },
  { value: 900, suffix: "M+", label: "Voters", sub: "Continental reach", icon: Users, cls: "lime" },
  { value: 70, suffix: "%", label: "Women", sub: "By selection, not quota", icon: Heart, cls: "crimson" },
  { value: 99.9999, suffix: "%", label: "Uptime", sub: "31.5s max downtime/yr", icon: Zap, cls: "amber-gold" },
  { value: 0, suffix: "", label: "Breaches", sub: "15+ years · clean record", icon: Award, cls: "emerald" },
  { value: 15, suffix: "+", label: "Languages", sub: "Native-grade execution", icon: Globe, cls: "denim" },
  { value: 300, suffix: "+", label: "Elite Clients", sub: "Sovereign-grade clientele", icon: Briefcase, cls: "purple" },
];

const RADAR_DATA = [
  { name: "Medicine", value: 95, fill: "oklch(0.60 0.22 25)" },
  { name: "Technology", value: 98, fill: "oklch(0.55 0.20 295)" },
  { name: "Politics", value: 92, fill: "oklch(0.55 0.12 240)" },
  { name: "Intelligence", value: 96, fill: "oklch(0.70 0.14 200)" },
  { name: "Security", value: 99, fill: "oklch(0.66 0.17 155)" },
  { name: "Governance", value: 94, fill: "oklch(0.78 0.15 75)" },
];

const BAR_DATA = [
  { year: "Y1-2", success: 65, label: "Foundation" },
  { year: "Y3-4", success: 72, label: "Establishment" },
  { year: "Y5-6", success: 78, label: "Acceleration" },
  { year: "Y7-8", success: 81, label: "Consolidation" },
  { year: "Y9-10", success: 83, label: "Sovereign" },
  { year: "Y11-12", success: 85, label: "Expansion" },
  { year: "Y13-15", success: 87, label: "Decade+ Evidence" },
];

const COLOR_BY: Record<string, string> = {
  red: "text-red",
  purple: "text-purple",
  emerald: "text-emerald",
  coral: "text-coral",
  cyan: "text-cyan",
  denim: "text-denim",
  lime: "text-lime",
  "amber-gold": "text-amber-gold",
  crimson: "text-crimson",
};

const BG_BY: Record<string, string> = {
  red: "from-red/10",
  purple: "from-purple/10",
  emerald: "from-emerald/10",
  coral: "from-coral/10",
  cyan: "from-cyan/10",
  denim: "from-denim/10",
  lime: "from-lime/10",
  "amber-gold": "from-amber-gold/10",
  crimson: "from-crimson/10",
};

export function ByTheNumbersSection() {
  return (
    <Section id="by-the-numbers" ariaLabel="By the Numbers" background="muted" pattern>
      <SectionHeader
        icon={Trophy}
        overline="Verified Operational Metrics"
        overlineClassName="text-amber-gold"
        pillClassName="glass-tinted-amber-gold border-amber-gold/20"
        title={
          <>
            Fifteen Years of <span className="text-gradient-premium">Results, Not Projections.</span>
          </>
        }
        subtitle="Every figure below is drawn from verified operational data. These are not projections. Not targets. Not estimates. Results — verified, operational, and drawn from engagements where the margin for error is measured in national stability."
      />

      {/* Visualizations row */}
      <div className="grid lg:grid-cols-[1fr_1.2fr] gap-5 mb-10">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          className="glass-card rounded-2xl p-6 lg:p-7"
        >
          <div className="flex items-start justify-between mb-4">
            <div>
              <div className="text-overline text-amber-gold mb-1">Domain Coverage</div>
              <div className="text-heading-md">Capability Index</div>
            </div>
            <Trophy className="size-5 text-amber-gold" />
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <RadialBarChart
              data={RADAR_DATA}
              startAngle={90}
              endAngle={-270}
              innerRadius="32%"
              outerRadius="100%"
            >
              <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
              <RadialBar
                background
                dataKey="value"
                cornerRadius={8}
                label={{ position: "insideStart", fill: "#fff", fontSize: 11 }}
              />
              <Tooltip
                contentStyle={{
                  background: "oklch(0.14 0.013 260)",
                  border: "1px solid oklch(0.22 0.010 260)",
                  borderRadius: 12,
                  fontSize: 12,
                }}
                labelStyle={{ color: "oklch(0.96 0.005 250)" }}
              />
            </RadialBarChart>
          </ResponsiveContainer>
          <div className="text-caption text-muted-foreground text-center mt-2">
            Coverage depth across the six strategic domains
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          className="glass-card rounded-2xl p-6 lg:p-7"
        >
          <div className="flex items-start justify-between mb-4">
            <div>
              <div className="text-overline text-emerald mb-1">Success Trajectory</div>
              <div className="text-heading-md">15 Years of Refinement</div>
            </div>
            <Activity className="size-5 text-emerald" />
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={BAR_DATA} margin={{ top: 8, right: 0, left: -16, bottom: 0 }}>
              <XAxis dataKey="year" tick={{ fill: "oklch(0.70 0.008 250)", fontSize: 10 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: "oklch(0.70 0.008 250)", fontSize: 10 }} axisLine={false} tickLine={false} domain={[60, 100]} />
              <Tooltip
                contentStyle={{
                  background: "oklch(0.14 0.013 260)",
                  border: "1px solid oklch(0.22 0.010 260)",
                  borderRadius: 12,
                  fontSize: 12,
                }}
                labelStyle={{ color: "oklch(0.96 0.005 250)" }}
                cursor={{ fill: "oklch(0.18 0.011 260 / 0.5)" }}
              />
              <Bar dataKey="success" radius={[8, 8, 0, 0]} fill="oklch(0.66 0.17 155)">
                {BAR_DATA.map((_, i) => (
                  <Cell key={i} fill={`oklch(0.66 0.17 155 ${0.6 + i * 0.05})`} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <div className="text-caption text-muted-foreground text-center mt-2">
            Campaign success rate (% wins of total engagements)
          </div>
        </motion.div>
      </div>

      {/* Stat grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 lg:gap-4">
        {METRICS.map((m, i) => (
          <motion.div
            key={m.label}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-30px" }}
            transition={{ duration: 0.4, delay: (i % 8) * 0.04 }}
            className={`glass-card rounded-xl p-4 lg:p-5 bg-gradient-to-br ${BG_BY[m.cls]} to-transparent group relative overflow-hidden`}
          >
            <div aria-hidden className="absolute -top-10 -right-10 size-28 rounded-full bg-current opacity-[0.05] blur-2xl group-hover:scale-150 transition-transform duration-700" />
            <m.icon className={`size-5 ${COLOR_BY[m.cls]} mb-3`} />
            <div className={`text-display-sm font-bold ${COLOR_BY[m.cls]} leading-none mb-1.5`}>
              <AnimatedStat value={m.value} suffix={m.suffix} />
            </div>
            <div className="text-caption font-semibold tracking-wide uppercase mb-1">
              {m.label}
            </div>
            <div className="text-caption text-muted-foreground leading-tight">
              {m.sub}
            </div>
          </motion.div>
        ))}
      </div>
    </Section>
  );
}
